import { Link } from "react-router-dom";
import { Mic2, Clapperboard, MapPin, ArrowRight } from "lucide-react";
import Seo from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { HOST, EXPANSION_CITIES } from "../data/content";

export default function Show() {
  return (
    <main className="pt-16" data-testid="show-page">
      <Seo title="The Show" description="Half stand-up. Half sketch. All L.A. Hosted by Todd Bridges, Laugh @ L.A. brings live stand-up and original scripted sketches together in one half-hour comedy show." />
      <section className="spotlight-bg border-b border-white/5">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-20 sm:py-28">
          <Reveal>
            <div className="flex flex-wrap items-center gap-3">
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#F59E0B]">The Show</p>
              <span className="inline-flex items-center rounded-full bg-gradient-to-r from-amber-500 to-yellow-600 px-4 py-1 text-xs font-bold uppercase tracking-wider text-black" data-testid="show-anniversary-badge">Celebrating 20 Years</span>
            </div>
            <h1 className="mt-3 font-display text-5xl sm:text-7xl text-white neon-red max-w-3xl">
              Half stand-up. Half sketch. All L.A.
            </h1>
            <p className="mt-6 max-w-2xl text-gray-300 leading-relaxed text-base sm:text-lg">
              Hosted by <span className="text-white font-semibold">Todd Bridges</span>, Laugh @ L.A. brings live stand-up and original scripted sketches together in one half-hour comedy show. One minute, a comic is working a room full of strangers. The next, a Hollywood director is trying to get through a scene with an actor who has his own ideas about the script.
            </p>
            <p className="mt-4 max-w-2xl text-gray-400 leading-relaxed">
              Produced by Providence Film Group, the original series features performers including Leslie Jones, Kel Mitchell, Debra Wilson, Sandy Brown, Bill Glass, and Esau McGraw. The show draws on a city where celebrity egos, nightlife, and the everyday indignities of trying to make a living are all fair game.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 py-20" data-testid="format-section">
        <div className="grid md:grid-cols-2 gap-6">
          <Reveal>
            <div className="h-full rounded-2xl border border-white/10 bg-[#181824]/70 p-8">
              <Mic2 className="text-[#E11D48]" size={32} />
              <h2 className="mt-4 font-display text-3xl text-white">Live Stand-Up</h2>
              <p className="mt-2 text-[#F59E0B] text-sm font-semibold">One mic. A room that lets you know.</p>
              <p className="mt-3 text-gray-300 leading-relaxed">
                The stand-up sets put you close enough to hear the crowd catch a joke. Each comic brings a different rhythm and point of view. Part of the fun is watching how they find their footing with the room, build a story, and land the line that brings everybody with them.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="h-full rounded-2xl border border-white/10 bg-[#181824]/70 p-8">
              <Clapperboard className="text-[#F59E0B]" size={32} />
              <h2 className="mt-4 font-display text-3xl text-white">Scripted Sketches</h2>
              <p className="mt-2 text-[#F59E0B] text-sm font-semibold">Hollywood gives us plenty to work with. So does everybody else.</p>
              <p className="mt-3 text-gray-300 leading-relaxed">
                The sketches take familiar situations and push them just far enough. A nightclub bathroom attendant charges luxury prices for a breath mint. A boss keeps tabs on how many times his employees chew their lunch. A trade school promises a fresh start in burglary and embezzlement.
              </p>
              <p className="mt-3 text-gray-300 leading-relaxed">
                Some setups are too good to use once. In <span className="text-white font-medium">Negotiating Actor</span>, the Sean Connery and 50 Cent parodies put different spins on a familiar Hollywood problem: the director needs the scene, and the star has other plans.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#12121A] border-y border-white/5" data-testid="host-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-20 grid lg:grid-cols-[380px_1fr] gap-12 items-center">
          <Reveal>
            <div className="relative">
              <div className="absolute -inset-3 rounded-2xl bg-gradient-to-tr from-[#E11D48]/25 to-[#F59E0B]/25 blur-xl" aria-hidden="true" />
              <img src={HOST.img} alt={`${HOST.name}, host of Laugh at L.A.`} className="relative rounded-2xl border border-white/10 w-full object-cover aspect-[3/4]" loading="lazy" />
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#F59E0B]">Your Host</p>
            <h2 className="mt-3 font-display text-5xl text-white">{HOST.name}</h2>
            <p className="mt-2 text-[#F59E0B] text-sm font-semibold uppercase tracking-widest">{HOST.credit}</p>
            <p className="mt-6 text-gray-300 leading-relaxed max-w-xl">
              A face television audiences grew up with, Todd Bridges anchors the night — introducing the comics, setting up the sketches, and keeping the room rolling between acts. He's the original host of Laugh @ L.A. and the connective tissue of the series.
            </p>
            <a href={HOST.imdb} target="_blank" rel="noopener noreferrer" data-testid="host-imdb-link"
              className="mt-6 inline-block rounded-full border border-[#F59E0B]/60 px-6 py-2.5 text-sm font-semibold text-[#F59E0B] hover:bg-[#F59E0B]/10 transition-colors">
              Todd Bridges on IMDb
            </a>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 py-20" data-testid="city-section">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#F59E0B]">A Comedy Show Built Around Its City</p>
          <div className="mt-6 max-w-2xl space-y-4 text-gray-300 leading-relaxed">
            <p>
              Los Angeles gives the original series its point of view. Hollywood is an easy target, but so are the bosses, salespeople, and self-appointed experts you run into far from a movie set. The stand-up brings in the comics' own perspectives. The sketches give the cast a chance to turn those familiar types into characters.
            </p>
            <p>
              That's the idea behind the broader Laugh @ format. Los Angeles is where it started. A future edition such as Laugh @ Atlanta would keep the mix of live stand-up and original sketches and turn the mic over to local comics and writers. They'd decide what's funny about their city, what outsiders get wrong, and which jokes only make sense if you've lived there.
            </p>
          </div>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 pb-20" data-testid="expansion-section">
        <Reveal>
          <h2 className="font-display text-4xl sm:text-5xl text-white max-w-3xl">L.A. is where it starts. Every city gets the mic.</h2>
          <p className="mt-4 max-w-2xl text-gray-400 leading-relaxed">
            The original Laugh at L.A. season is complete. The Laugh @ format is built to travel — these editions are in development as commissioning opportunities.
          </p>
        </Reveal>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {EXPANSION_CITIES.map((c, i) => (
            <Reveal key={c.city} delay={i * 0.05}>
              <div className="rounded-xl border border-white/10 bg-[#181824]/70 p-6 h-full hover:border-[#E11D48]/50 transition-colors" data-testid={`city-${c.city.toLowerCase().replace(/\s+/g, "-")}`}>
                <MapPin size={20} className="text-[#E11D48]" />
                <p className="mt-3 font-display text-2xl text-white">Laugh @ {c.city}</p>
                <p className="mt-2 text-sm text-gray-400">{c.note}</p>
                <p className="mt-3 text-xs font-semibold uppercase tracking-widest text-[#F59E0B]">In development</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="relative overflow-hidden border-t border-white/5" data-testid="get-into-show-section">
        <div className="absolute inset-0 bg-gradient-to-r from-[#E11D48]/15 via-transparent to-[#F59E0B]/15" aria-hidden="true" />
        <div className="relative mx-auto max-w-4xl px-4 sm:px-6 py-20 text-center">
          <Reveal>
            <h2 className="font-display text-4xl sm:text-6xl text-white">Get Into the Show</h2>
            <p className="mt-4 text-gray-300 max-w-xl mx-auto">
              Watch the official trailer, explore the sketch lineup, and go behind the scenes to see the cast in rehearsal. Join the list for new clips and updates on what's next for Laugh @.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <Link to="/watch" data-testid="show-cta-trailer"
                className="inline-flex items-center gap-2 rounded-full bg-[#E11D48] px-7 py-3.5 font-semibold text-white shadow-[0_0_25px_rgba(225,29,72,0.5)] transition-all hover:bg-[#F43F5E]">
                Watch the Trailer
              </Link>
              <Link to="/sketches" data-testid="show-cta-sketches"
                className="inline-flex items-center gap-2 rounded-full border border-[#F59E0B]/60 px-7 py-3.5 font-semibold text-[#F59E0B] transition-colors hover:bg-[#F59E0B]/10">
                Explore the Sketches <ArrowRight size={16} />
              </Link>
              <Link to="/join" data-testid="show-cta-join"
                className="inline-flex items-center gap-2 rounded-full border border-white/25 px-7 py-3.5 font-semibold text-white transition-colors hover:border-[#F59E0B] hover:text-[#F59E0B]">
                Join the List
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
